import { type ReactNode, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router';
import {
  LayoutDashboard,
  Users,
  Star,
  Award,
  FileText,
  CalendarClock,
  MessageSquare,
  Gavel,
  ShieldCheck,
  ScrollText,
  Menu,
  X,
  LogOut,
  Sun,
  Moon,
  Settings,
  ClipboardCheck,
  ClipboardX,
  Network,
  BookUser,
  Building2,
  MessagesSquare,
  Search,
} from 'lucide-react';
import { useAuthStore } from '../store/auth.js';
import { api } from '../lib/api.js';
import { OfflineIndicator } from './OfflineIndicator.js';
import { InstallPrompt } from './InstallPrompt.js';
import { NotificationBell } from '../features/notifications/components/NotificationBell.js';
import { CHAT_ENABLED } from '@shared/features';
import { useChatUnreadCount } from '../features/chat/ChatClientProvider.js';
import { PersonAvatar } from './PersonAvatar.js';

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  roles: ('Admin' | 'Manager' | 'Employee')[];
}

const navItems: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: <LayoutDashboard className="h-5 w-5" />, roles: ['Admin', 'Manager', 'Employee'] },
  { to: '/directory', label: 'Directory', icon: <BookUser className="h-5 w-5" />, roles: ['Admin', 'Manager', 'Employee'] },
  { to: '/vendors', label: 'Vendors', icon: <Building2 className="h-5 w-5" />, roles: ['Admin', 'Manager', 'Employee'] },
  { to: '/employees', label: 'Employees', icon: <Users className="h-5 w-5" />, roles: ['Admin', 'Manager', 'Employee'] },
  { to: '/org-chart', label: 'Org chart', icon: <Network className="h-5 w-5" />, roles: ['Admin', 'Manager', 'Employee'] },
  { to: '/reviews', label: 'Reviews', icon: <Star className="h-5 w-5" />, roles: ['Admin', 'Manager', 'Employee'] },
  { to: '/recognition', label: 'Recognition', icon: <Award className="h-5 w-5" />, roles: ['Admin', 'Manager', 'Employee'] },
  { to: '/time-off', label: 'Time off', icon: <CalendarClock className="h-5 w-5" />, roles: ['Admin', 'Manager', 'Employee'] },
  { to: '/chat', label: 'Chat', icon: <MessagesSquare className="h-5 w-5" />, roles: ['Admin', 'Manager', 'Employee'] },
  { to: '/onboarding', label: 'Onboarding', icon: <ClipboardCheck className="h-5 w-5" />, roles: ['Admin', 'Manager'] },
  { to: '/offboarding', label: 'Offboarding', icon: <ClipboardX className="h-5 w-5" />, roles: ['Admin', 'Manager'] },
  { to: '/naid', label: 'NAID', icon: <ShieldCheck className="h-5 w-5" />, roles: ['Admin', 'Manager'] },
  { to: '/conversations', label: 'Conversations', icon: <MessageSquare className="h-5 w-5" />, roles: ['Admin', 'Manager', 'Employee'] },
  { to: '/disciplinary', label: 'Actions', icon: <Gavel className="h-5 w-5" />, roles: ['Admin', 'Manager', 'Employee'] },
  { to: '/documents', label: 'Documents', icon: <FileText className="h-5 w-5" />, roles: ['Admin', 'Manager'] },
  { to: '/audit', label: 'Audit log', icon: <ScrollText className="h-5 w-5" />, roles: ['Admin'] },
  { to: '/settings', label: 'Settings', icon: <Settings className="h-5 w-5" />, roles: ['Admin', 'Manager', 'Employee'] },
];

const roleBadge: Record<string, { label: string; tone: string }> = {
  Admin: { label: 'Admin', tone: 'bg-[var(--color-accent-soft)] text-[var(--color-accent)]' },
  Manager: { label: 'Manager', tone: 'bg-[var(--color-secondary-soft)] text-[var(--color-ink)]' },
  Employee: { label: 'Employee', tone: 'bg-[var(--color-paper-2)] text-[var(--color-ink)]' },
};

const APP_TITLE = 'Vibe Wireless HR';

export function Layout({ children }: { children: ReactNode }) {
  const {
    user,
    actualUser,
    viewAsRole,
    setViewAsRole,
    theme,
    toggleTheme,
  } = useAuthStore();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [navSearch, setNavSearch] = useState('');
  const visibleItems = (user ? navItems.filter((item) => item.roles.includes(user.role)) : navItems)
    .filter((item) => CHAT_ENABLED || item.to !== '/chat');

  const filteredItems = visibleItems.filter((item) =>
    item.label.toLowerCase().includes(navSearch.trim().toLowerCase()),
  );
  const currentPage = visibleItems.find((item) =>
    item.to === '/' ? location.pathname === '/' : location.pathname.startsWith(item.to),
  )?.label ?? 'Workspace';

  const chatUnread = useChatUnreadCount();

  const renderNav = (opts?: { onNavigate?: () => void }) => (
    <nav className="flex flex-col gap-1" aria-label="Main navigation">
      <label className="app-nav-search">
        <Search className="h-4 w-4 shrink-0" aria-hidden="true" />
        <input
          type="search"
          aria-label="Find a page"
          placeholder="Find a page…"
          value={navSearch}
          onChange={(event) => setNavSearch(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') setNavSearch('');
          }}
        />
      </label>
      {filteredItems.length === 0 && (
        <p className="app-nav-empty" role="status">No pages match your search.</p>
      )}
      {filteredItems.map((item) => {
        const showBadge = item.to === '/chat' && chatUnread > 0;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            onClick={() => {
              setNavSearch('');
              opts?.onNavigate?.();
            }}
            className="app-nav-link flex items-center gap-2 px-2 py-1.5 text-sm font-medium"
          >
            <span className="app-nav-link__icon">{item.icon}</span>
            <span className="flex-1">{item.label}</span>
            {showBadge && (
              <span className="app-nav-badge" aria-label={`${chatUnread} unread`}>
                {chatUnread > 99 ? '99+' : chatUnread}
              </span>
            )}
          </NavLink>
        );
      })}
    </nav>
  );

  const renderRolePreview = () => {
    if (actualUser?.role !== 'Admin') return null;

    return (
      <div className="app-role-preview">
        <label className="app-role-preview-label" htmlFor="role-preview">
          View as
        </label>
        <select
          id="role-preview"
          className="app-role-preview-select"
          value={viewAsRole ?? 'Admin'}
          onChange={(event) => {
            const nextRole = event.target.value;
            setViewAsRole(nextRole === 'Manager' || nextRole === 'Employee' ? nextRole : null);
          }}
        >
          <option value="Admin">Admin</option>
          <option value="Manager">Manager</option>
          <option value="Employee">Employee</option>
        </select>
      </div>
    );
  };

  const UserAvatar = (
    <PersonAvatar name={user?.name ?? '?'} src={user?.pictureUrl} size="sm" />
  );

  const signOut = async () => {
    await api.post('/api/auth/signout');
    window.location.reload();
  };

  return (
    <div className="app-shell">
      {/* Desktop icon rail */}
      <aside
        className="app-rail fixed inset-y-0 left-0 z-40 hidden w-[5.5rem] flex-col md:flex pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]"
      >
        <Link to="/" className="app-rail-brand" title="Go to home" aria-label="Go to home">
          <ShieldCheck className="h-6 w-6" />
        </Link>
        <nav className="app-rail-nav" aria-label="Main navigation">
          {visibleItems.map((item) => {
            const showBadge = item.to === '/chat' && chatUnread > 0;
            return (
              <NavLink key={item.to} to={item.to} end={item.to === '/'} className="app-rail-link" title={item.label}>
                <span className="app-rail-icon">
                  {item.icon}
                  {showBadge && (
                    <span className="app-rail-count" aria-label={`${chatUnread} unread`}>
                      {chatUnread > 99 ? '99+' : chatUnread}
                    </span>
                  )}
                </span>
                <span className="app-rail-label">{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
      </aside>

      {/* Desktop top bar */}
      <header className="app-topbar fixed inset-x-0 top-0 z-30 hidden md:flex md:pl-[5.5rem]">
        <div className="app-topbar-title">
          <span className="app-brand-name">{APP_TITLE}</span>
          <span className="app-topbar-page">{currentPage}</span>
        </div>
        <div className="app-topbar-actions">
          {renderRolePreview()}
          <NotificationBell />
          <button
            type="button"
            onClick={toggleTheme}
            className="app-icon-button"
            aria-label="Toggle theme"
            title="Toggle theme"
          >
            {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          {user && (
            <div className="app-user-chip" title={`${user.name} · ${roleBadge[user.role]?.label}`}>
              <span className="app-user-name truncate text-sm font-semibold">{user.name}</span>
              {UserAvatar}
            </div>
          )}
          <button
            type="button"
            onClick={signOut}
            className="app-icon-button"
            aria-label="Sign out"
            title="Sign out"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </header>

      {/* Mobile top bar */}
      <header className="app-sidebar sticky top-0 z-30 flex items-center justify-between border-b safe-px safe-pt pb-3 md:hidden">
        <Link
          to="/"
          className="flex min-w-0 items-center gap-2 outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--color-focus)]"
          title="Go to home"
          aria-label="Go to home"
        >
          <ShieldCheck className="h-5 w-5 shrink-0 text-[var(--color-accent)]" />
          <span className="app-brand-name truncate font-semibold">{APP_TITLE}</span>
        </Link>
        <div className="flex items-center gap-1">
          <NotificationBell />
          <button
            type="button"
            onClick={toggleTheme}
            className="app-icon-button"
            aria-label="Toggle theme"
          >
            {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>
          <button
            type="button"
            onClick={() => setMobileNavOpen(true)}
            className="app-icon-button"
            aria-label="Open navigation menu"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </header>

      {/* Mobile drawer */}
      {mobileNavOpen && (
        <div className="fixed inset-0 z-50 md:hidden" role="dialog" aria-modal="true">
          <div
            className="absolute inset-0 bg-[var(--color-overlay)]"
            onClick={() => setMobileNavOpen(false)}
            aria-hidden="true"
          />
          <div className="app-sidebar absolute inset-y-0 left-0 flex w-[min(18rem,100%)] flex-col pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)]">
            <div className="flex items-center justify-between border-b border-[var(--color-rule)] px-4 py-3 sm:px-6 sm:py-4">
              <Link
                to="/"
                onClick={() => setMobileNavOpen(false)}
                className="flex min-w-0 items-center gap-2 outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--color-focus)]"
                title="Go to home"
                aria-label="Go to home"
              >
                <ShieldCheck className="h-5 w-5 shrink-0 text-[var(--color-accent)]" />
                <span className="app-brand-name truncate font-semibold">{APP_TITLE}</span>
              </Link>
              <button
                type="button"
                onClick={() => setMobileNavOpen(false)}
                className="app-icon-button"
                aria-label="Close navigation"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto overscroll-contain px-3 py-4">
              {renderNav({ onNavigate: () => setMobileNavOpen(false) })}
              {renderRolePreview()}
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="md:pl-[5.5rem] md:pt-16">
        <OfflineIndicator />
        <InstallPrompt />
        <main
          className="app-main mx-auto safe-px py-4 sm:py-6 md:px-6 md:py-8 safe-pb"
          key={location.pathname}
        >
          {actualUser?.role === 'Admin' && viewAsRole && (
            <div className="app-preview-banner" role="status">
              <span>Previewing the {viewAsRole} experience · UI only.</span>
              <button type="button" onClick={() => setViewAsRole(null)}>
                Exit preview
              </button>
            </div>
          )}
          {children}
        </main>
      </div>
    </div>
  );
}
